import type { Repo } from '../data'
import type { Game, ID } from '../domain/types'

/**
 * Drafts: games left in progress but not currently being scored.
 *
 * A game is a draft when its status is still `in_progress` but it is not the
 * current game. That happens when the scorer leaves via the exit control and
 * chooses "Keep as draft" -- typically because they opened the wrong game, or
 * had to stop and want to come back to it.
 */

/** Drafts are purged after this long without being touched. */
export const DRAFT_TTL_DAYS = 7

export function isDraft(game: Game, currentGameId: ID | null): boolean {
  return game.status === 'in_progress' && game.id !== currentGameId
}

export function listDrafts(games: Game[], currentGameId: ID | null): Game[] {
  return games.filter((g) => isDraft(g, currentGameId)).sort((a, b) => b.updatedAt - a.updatedAt)
}

/**
 * Delete a game and its events outright.
 *
 * Refuses a finished game. A `final` game is the season's record -- it has a
 * score, it may already have been read by the reports, and nothing in this
 * screen should be able to destroy it. Discard exists for a game opened by
 * mistake, not as a general delete.
 */
export async function discardGame(repo: Repo, game: Game): Promise<void> {
  if (game.status === 'final') {
    throw new Error('Refusing to discard a completed game')
  }
  // deleteGame removes the game and cascades its events in both repo
  // implementations. Nothing here has ever been written to the server: the PWA
  // does not push stat events yet, so a local delete is genuinely complete.
  await repo.deleteGame(game.id)
}

/**
 * Remove drafts nobody has come back to.
 *
 * Keyed on updatedAt, not the game date or createdAt: a draft someone edited
 * yesterday is live work regardless of which fixture it belongs to, and a game
 * date can be weeks in the past for a late catch-up.
 */
export async function purgeStaleDrafts(
  repo: Repo,
  games: Game[],
  currentGameId: ID | null,
  now = Date.now(),
  ttlDays = DRAFT_TTL_DAYS,
): Promise<ID[]> {
  const cutoff = now - ttlDays * 24 * 60 * 60 * 1000
  const stale = listDrafts(games, currentGameId).filter((g) => g.updatedAt < cutoff)
  for (const g of stale) await repo.deleteGame(g.id)
  return stale.map((g) => g.id)
}
